/**
 * The floating mirror of a live browser tab.
 *
 * Ported from t3code (`apps/web/src/components/preview/ThreadPreviewMiniPlayer.tsx`,
 * `BrowserMiniPlayer`, MIT, pin `e3e7cc3f`). Adapted onto forge's WebRTC
 * browser runtime: the floater mounts its own `RemoteWebRtcBrowserSurface`
 * for the mirrored tab, a fresh display client on the same attach path the
 * panel uses, instead of upstream's webview reparenting slot; its connecting
 * and reconnecting states read through the surface's own status handling.
 * Display-session reconciliation stays with the panel: the thread's WS feed
 * maintains the store this floater reads, and the surface's
 * `onDisplayUpdated` merge is a panel concern the floater does not repeat.
 *
 * @module BrowserMiniPlayer
 */
import type { BrowserSessionSnapshot, ScopedThreadRef } from "@cmmd-forge/contracts";
import { scopedThreadKey } from "@cmmd-forge/client-runtime";
import { shouldAttachRemotePixelDisplay } from "@cmmd-forge/shared/browserPresentation";

import { RemoteWebRtcBrowserSurface } from "~/components/browser/RemoteWebRtcBrowserSurface";
import { remoteWebRtcBrowserSurfaceInstanceKey } from "~/components/BrowserPreviewPanel.remoteWebRtcDisplay";
import { useRightPanelStore } from "~/rightPanelStore";
import { selectBrowserThreadState, useBrowserStateStore } from "~/browserStateStore";
import { toastManager } from "~/components/ui/toast";

import { BrowserMiniPlayerShell } from "./BrowserMiniPlayerShell";
import { BrowserAgentCursorLayer } from "~/browser/AgentBrowserCursor";
import { useBrowserRecordingStore } from "~/browser/browserRecording";
import { isUsableBrowserMiniPlayerSource } from "./browserMiniPlayerLayout";
import { useBrowserMiniPlayerStore, type BrowserMiniPlayerState } from "./browserMiniPlayerStore";

export interface BrowserMiniPlayerProps {
  readonly threadRef: ScopedThreadRef;
  readonly miniPlayer: BrowserMiniPlayerState;
}

/** Floats the thread's browser tab over the chat column. */
export function BrowserMiniPlayer({ threadRef, miniPlayer }: BrowserMiniPlayerProps) {
  const threadState = useBrowserStateStore((state) =>
    selectBrowserThreadState(state.browserStateByThreadKey, threadRef),
  );
  const recording = useBrowserRecordingStore((state) =>
    Boolean(state.byTabId[miniPlayer.source.tabId]),
  );
  const session = threadState?.session ?? null;
  if (!session || !isUsableBrowserMiniPlayerSource(session.viewport)) return null;
  return (
    <BrowserMiniPlayerSurface
      threadRef={threadRef}
      miniPlayer={miniPlayer}
      recording={recording}
      session={session}
      displayIceCandidatesByClientId={threadState?.displayIceCandidatesByClientId}
    />
  );
}

function BrowserMiniPlayerSurface({
  threadRef,
  miniPlayer,
  recording,
  session,
  displayIceCandidatesByClientId,
}: BrowserMiniPlayerProps & {
  readonly recording: boolean;
  readonly session: BrowserSessionSnapshot;
  readonly displayIceCandidatesByClientId:
    | NonNullable<ReturnType<typeof selectBrowserThreadState>>["displayIceCandidatesByClientId"]
    | undefined;
}) {
  const tabId = miniPlayer.source.tabId;

  const openInPanel = () => {
    useBrowserMiniPlayerStore.getState().close(threadRef);
    useRightPanelStore.getState().openBrowser(scopedThreadKey(threadRef), tabId);
  };

  return (
    <BrowserMiniPlayerShell
      threadRef={threadRef}
      miniPlayer={miniPlayer}
      sourceSize={session.viewport}
      label="Floating browser preview"
      recording={recording}
      onOpenInPanel={openInPanel}
    >
      {() => (
        <>
          {session.display.transport === "remote-webrtc" &&
          shouldAttachRemotePixelDisplay(session.display) ? (
            <RemoteWebRtcBrowserSurface
              key={remoteWebRtcBrowserSurfaceInstanceKey({
                browserId: session.browserId,
                sessionId: session.sessionId,
                viewport: session.viewport,
                display: session.display,
              })}
              active
              environmentId={threadRef.environmentId}
              threadId={threadRef.threadId}
              browserId={session.browserId}
              sessionId={session.sessionId}
              tabId={tabId}
              userId={session.userId}
              viewport={session.viewport}
              {...(session.display.devicePixelRatio !== undefined
                ? { devicePixelRatio: session.display.devicePixelRatio }
                : {})}
              {...(session.display.iceServers ? { iceServers: session.display.iceServers } : {})}
              remoteIceCandidatesByClientId={displayIceCandidatesByClientId}
              onDisplayUpdated={() => undefined}
              onError={(error) => {
                toastManager.add({
                  type: "error",
                  title: "Floating preview disconnected",
                  description:
                    error instanceof Error ? error.message : "The display connection failed.",
                });
              }}
            />
          ) : (
            <div
              className="pointer-events-none flex h-full w-full items-center justify-center bg-muted text-xs text-muted-foreground"
              data-browser-mini-player-reconnecting="true"
            >
              Reconnecting preview…
            </div>
          )}
          <BrowserAgentCursorLayer threadRef={threadRef} tabId={tabId} />
        </>
      )}
    </BrowserMiniPlayerShell>
  );
}
